import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Download, Loader2, AlertTriangle, Settings } from "lucide-react";
import { cn } from "@lib/utils";
import type { ProcessingStatus, VideoPayload } from "~/lib/uploads";

interface VideoPlayerProps {
  video: VideoPayload;
  processingStatus: ProcessingStatus;
  filename?: string;
  className?: string;
}

// A rung the manual quality menu can select. `level` is the hls.js level index;
// `-1` is the sentinel hls.js uses for automatic (ABR) selection.
interface QualityOption {
  level: number;
  label: string;
}

const AUTO_LEVEL = -1;
const NATIVE_HLS_MIME = "application/vnd.apple.mpegurl";

// Largest progressive MP4 (serializer ships them largest-first) — the download
// artifact and the fallback source when HLS can't run.
function largestProgressive(video: VideoPayload) {
  return video.renditions[0] ?? null;
}

// The non-HLS playback source, by tier: the largest completed progressive
// rendition if one exists, otherwise the raw original when it's browser-safe.
// This is what lets playback ungate the instant the upload completes — before
// any rung finishes — and then upgrade to the progressive rung as it lands.
function nonHlsSource(video: VideoPayload): { url: string; label: string } | null {
  const progressive = largestProgressive(video);
  if (progressive) return { url: progressive.url, label: "progressive" };
  if (video.original.playable && video.original.url) {
    return { url: video.original.url, label: "original" };
  }
  return null;
}

function levelLabel(height: number | null | undefined): string {
  return height ? `${height}p` : "Auto";
}

export function VideoPlayer({
  video,
  processingStatus,
  filename,
  className,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  // The progressive URL currently wired into the media element. Lets the
  // upgrade effect tell a genuine rung swap (720p → 1080p) apart from the
  // initial mount, so it only forces a reload — and reseek — on a real change.
  const progressiveUrlRef = useRef<string | null>(null);
  // Position to restore after the next resource selection (a progressive
  // reload or an hls.js attach), so a mid-watch upgrade never restarts at 0:00.
  const resumeTimeRef = useRef<number | null>(null);

  const [qualityOptions, setQualityOptions] = useState<QualityOption[]>([]);
  const [currentLevel, setCurrentLevel] = useState<number>(AUTO_LEVEL);
  const [menuOpen, setMenuOpen] = useState(false);
  // Flip to the plain progressive <source> path when HLS is unavailable or a
  // fatal hls.js error fires. Native-HLS Safari plays the master directly.
  const [useProgressive, setUseProgressive] = useState(false);

  const progressive = largestProgressive(video);
  const fallbackSource = nonHlsSource(video);
  const isProcessing = processingStatus === "processing";
  const isFailed = processingStatus === "failed";
  const isReady = processingStatus === "ready";
  // Playback ungates at "any version is playable": as soon as the original is
  // browser-compatible, a progressive rendition completes, or the HLS master
  // exists, play it — even mid-processing. The spinner-over-poster state is
  // reserved for a video with nothing to play yet.
  const canPlay = isReady || (isProcessing && video.playable);
  // "Still improving" text shows while processing and playing something that
  // isn't the full ladder yet (the original, or a partial set of rungs).
  const stillImproving = isProcessing && video.playable;
  const hlsMaster = video.hls_master_url;

  useEffect(() => {
    const el = videoRef.current;
    if (!canPlay || !el) return;

    // No HLS master (e.g. all HLS rungs were skipped): progressive only.
    if (!hlsMaster) {
      setUseProgressive(true);
      return;
    }

    const nativeHls = el.canPlayType(NATIVE_HLS_MIME) !== "";
    if (nativeHls) {
      // Safari plays HLS natively; hls.js is neither needed nor supported here.
      el.src = hlsMaster;
      setUseProgressive(false);
      return () => {
        el.removeAttribute("src");
        el.load();
      };
    }

    if (!Hls.isSupported()) {
      setUseProgressive(true);
      return;
    }

    // Attaching hls.js and loading a fresh manifest resets the element to 0.
    // Capture where the viewer was (e.g. mid-watch on the progressive source
    // when the master lands) so MANIFEST_PARSED can put them back.
    const wasPlaying = !el.paused;
    if (el.currentTime > 0) {
      resumeTimeRef.current = el.currentTime;
    }

    const hls = new Hls({ enableWorker: true });
    hlsRef.current = hls;
    setUseProgressive(false);

    hls.on(Hls.Events.MANIFEST_PARSED, (_evt, data) => {
      const options: QualityOption[] = [
        { level: AUTO_LEVEL, label: "Auto" },
        ...data.levels.map((lvl, index) => ({
          level: index,
          label: levelLabel(lvl.height),
        })),
      ];
      setQualityOptions(options);
      // Default to automatic ABR; hls.js measures throughput and switches rungs.
      hls.currentLevel = AUTO_LEVEL;
      setCurrentLevel(AUTO_LEVEL);

      // Restore the pre-hop position so the progressive → HLS switch is seamless.
      const resumeAt = resumeTimeRef.current;
      resumeTimeRef.current = null;
      if (resumeAt != null && resumeAt > 0) {
        el.currentTime = resumeAt;
        if (wasPlaying) void el.play();
      }
    });

    hls.on(Hls.Events.LEVEL_SWITCHED, (_evt, data) => {
      // Reflect the level hls.js is actually playing so the menu tracks auto
      // switches. currentLevel stays the manual/auto selection; this is the
      // effective rung.
      el.setAttribute("data-hls-level", String(data.level));
    });

    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (!data.fatal) return;
      // A fatal hls.js error is unrecoverable for this media element — tear it
      // down and fall back to the progressive MP4 source.
      hls.destroy();
      hlsRef.current = null;
      setUseProgressive(true);
    });

    hls.loadSource(hlsMaster);
    hls.attachMedia(el);

    return () => {
      hls.destroy();
      hlsRef.current = null;
    };
  }, [canPlay, hlsMaster]);

  const progressiveUrl = useProgressive ? fallbackSource?.url ?? null : null;

  // Live rung upgrades on the progressive path (original → 720p → 1080p).
  // Changing a <source>'s src after resource selection is a no-op per spec, so
  // when the chosen URL actually changes on an already-mounted element we force
  // a reload — preserving currentTime and play/pause so the viewer keeps
  // watching from where they were, at higher quality.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const previousUrl = progressiveUrlRef.current;
    progressiveUrlRef.current = progressiveUrl;

    // Initial mount of a source (nothing → url) needs no reload; the element
    // selects it on its own. Only a genuine swap between two URLs does.
    if (!progressiveUrl || !previousUrl || previousUrl === progressiveUrl) return;

    const resumeAt = el.currentTime;
    const wasPlaying = !el.paused;
    el.load();
    if (resumeAt > 0) {
      el.currentTime = resumeAt;
      if (wasPlaying) void el.play();
    }
  }, [progressiveUrl]);

  // Manual quality override. Setting currentLevel is all hls.js needs — it
  // preserves currentTime natively across the switch (no reload, no reseek).
  function selectLevel(level: number) {
    setCurrentLevel(level);
    setMenuOpen(false);
    if (hlsRef.current) {
      hlsRef.current.currentLevel = level;
    }
  }

  const showQualityMenu =
    canPlay && !useProgressive && qualityOptions.length > 1;

  return (
    <div
      data-testid="video-player"
      data-processing-status={processingStatus}
      className={cn("relative overflow-hidden rounded-xl bg-base-900", className)}
    >
      <video
        ref={videoRef}
        data-testid="video-player-element"
        controls={canPlay}
        poster={video.poster_url ?? undefined}
        className="aspect-video w-full bg-black"
        playsInline
      >
        {/* The non-HLS <source> is the HLS fallback and the JS-hostile path:
            the largest progressive rung if one exists, else the browser-safe
            original. Present whenever we're not driving the element via hls.js. */}
        {useProgressive && fallbackSource && (
          <source src={fallbackSource.url} type="video/mp4" />
        )}
      </video>

      {showQualityMenu && (
        <div className="absolute bottom-14 right-3" data-testid="quality-menu">
          <button
            type="button"
            aria-label="Quality"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-xs text-white hover:bg-black/80"
          >
            <Settings className="size-3.5" />
            {qualityOptions.find((o) => o.level === currentLevel)?.label ?? "Auto"}
          </button>
          {menuOpen && (
            <ul className="absolute bottom-full right-0 mb-1 min-w-24 overflow-hidden rounded-md bg-black/90 py-1 text-xs text-white">
              {qualityOptions.map((option) => (
                <li key={option.level}>
                  <button
                    type="button"
                    data-testid="quality-option"
                    data-level={option.level}
                    aria-pressed={option.level === currentLevel}
                    onClick={() => selectLevel(option.level)}
                    className={cn(
                      "block w-full px-3 py-1 text-left hover:bg-white/10",
                      option.level === currentLevel && "font-semibold text-primary-300"
                    )}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isProcessing && !video.playable && (
        <div
          data-testid="video-processing-overlay"
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-base-900/70 text-white"
        >
          <Loader2 className="size-6 animate-spin" />
          <span className="text-sm">Processing…</span>
        </div>
      )}

      {stillImproving && (
        <p
          data-testid="video-quality-improving"
          className="px-3 py-2 text-xs text-neutral-400"
        >
          Still processing — video playback will improve
        </p>
      )}

      {isFailed && (
        <div
          data-testid="video-failed-overlay"
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-base-900/80 px-6 text-center text-white"
        >
          <AlertTriangle className="size-6 text-error-400" />
          <span className="text-sm font-medium">We couldn&apos;t process this video.</span>
          {progressive && (
            <a
              href={progressive.url}
              download={filename}
              data-testid="video-download-link"
              className="text-xs text-primary-300 underline"
            >
              Download the original
            </a>
          )}
        </div>
      )}

      {isReady && progressive && (
        <a
          href={progressive.url}
          download={filename}
          data-testid="video-download-link"
          aria-label="Download video"
          className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-xs text-white hover:bg-black/80"
        >
          <Download className="size-3.5" />
          Download
        </a>
      )}
    </div>
  );
}
